/**
 * The spring that settles a table swipe — and the reason there is only ONE
 * source of truth for where the carousel is at any instant.
 *
 * ## Why a spring and not a duration
 *
 * The settle used to be a WAAPI keyframe animation: `from` px, `to` px, a
 * cubic-bezier, a duration. Three things follow from that shape, and all three
 * were bugs players could feel:
 *
 *   1. A keyframe animates toward a TARGET from a snapshot. It cannot be
 *      grabbed mid-flight and redirected, so the app had to lock input out for
 *      the length of the settle (see tableSwitchTransition). Swipe twice
 *      quickly and the second swipe was simply discarded — which reads as lag,
 *      because the interface stopped answering.
 *   2. The animation lived on the element, so nothing else could follow it.
 *      The indicator dot tracked the drag and then FROZE the moment the finger
 *      lifted, because the channel it listened to stopped publishing.
 *   3. Duration and gesture velocity are different quantities. Deriving one
 *      from the other (distance / speed, clamped) approximates a spring badly:
 *      the seam between "your finger was moving it" and "the app is moving it"
 *      stays visible however the curve is tuned.
 *
 * A spring has no duration. It has a position, a velocity and a target, and it
 * integrates forward from wherever it currently is. Redirecting it mid-flight
 * is just changing `target` — position and velocity carry through, so there is
 * no discontinuity to hide and nothing to lock out. That is the whole reason
 * Apple's fluid-interface work is built on springs rather than curves.
 *
 * ## Critically damped, deliberately
 *
 * `DAMPING_RATIO = 1` — no overshoot, ever. Bounce is right for a card you
 * flicked across open space; it is wrong here. The panels are edge to edge, so
 * an overshoot at the end of a commit does not read as momentum, it briefly
 * exposes the table AFTER the one you asked for. A poker client showing you a
 * sliver of the wrong felt looks broken, not lively.
 *
 * ## Units
 *
 * Milliseconds and pixels throughout, matching the gesture layer (px/ms) so a
 * release velocity can be handed to the spring untouched. That handoff is the
 * point: the settle continues the gesture at exactly the speed the finger left
 * it, which is what removes the seam.
 */

/** No overshoot — see the docblock. */
export const DAMPING_RATIO = 1;

/**
 * How quickly the spring converges, in ms. NOT a duration — the motion runs
 * until it rests, which depends on distance and release velocity too. Apple
 * ships 0.3-0.4s for repositioning; 340ms sat in that band but read as
 * sluggish in on-device testing of the poker table swap (2026-08-20) - a
 * player mid-session wants the next felt NOW. 220ms keeps the spring's
 * continuity (no snap, still redirectable) while landing near-instant.
 */
export const RESPONSE_MS = 220;

/** Below this distance AND speed the spring is at rest and stops publishing. */
export const REST_DISTANCE_PX = 0.5;
export const REST_VELOCITY_PX_PER_MS = 0.02; // 20px/s

/**
 * Longest frame the integrator will accept in one step. A backgrounded tab or a
 * long task can hand us a 2000ms delta; integrating that in one step throws the
 * spring across the screen. Longer frames are split into substeps of at most
 * this, which keeps the simulation stable no matter how badly the main thread
 * stalls.
 */
export const MAX_STEP_MS = 4;

export type SpringState = { x: number; v: number };

/**
 * Advance a spring by `elapsedMs` toward `target`.
 *
 * Semi-implicit Euler, substepped. Pure — the caller owns the clock, which is
 * what makes the motion testable without a browser.
 */
export const stepSpring = (
  state: SpringState,
  target: number,
  elapsedMs: number,
  responseMs: number = RESPONSE_MS,
  dampingRatio: number = DAMPING_RATIO
): SpringState => {
  const omega = (2 * Math.PI) / Math.max(1, responseMs);
  const stiffness = omega * omega;
  const damping = 2 * dampingRatio * omega;

  let { x, v } = state;
  let remaining = Math.max(0, elapsedMs);

  while (remaining > 0) {
    const dt = Math.min(MAX_STEP_MS, remaining);
    const acceleration = -stiffness * (x - target) - damping * v;
    v += acceleration * dt;
    x += v * dt;
    remaining -= dt;
  }

  return { x, v };
};

/** True once the spring is close enough, and slow enough, to stop. */
export const isSpringAtRest = (state: SpringState, target: number): boolean =>
  Math.abs(state.x - target) < REST_DISTANCE_PX && Math.abs(state.v) < REST_VELOCITY_PX_PER_MS;

export type SpringDriver = {
  /**
   * Point the spring at a new target. Position and velocity are PRESERVED, so
   * calling this mid-flight redirects the motion instead of restarting it —
   * the property that makes a settle interruptible.
   */
  setTarget: (target: number) => void;
  /** Adopt an externally-driven position (a finger has taken the track over). */
  setPosition: (x: number, v?: number) => void;
  /** Current position — the presentation value to animate from on interrupt. */
  position: () => number;
  velocity: () => number;
  running: () => boolean;
  /** Stop where it is, without firing onRest. */
  stop: () => void;
};

type DriverOptions = {
  /** Called every frame with the spring's position. */
  onFrame: (x: number) => void;
  /** Called once when the spring settles, with the resting position. */
  onRest: (x: number) => void;
  /** Skip the simulation entirely and jump to target (reduced motion). */
  immediate?: () => boolean;
  responseMs?: number;
};

/**
 * rAF-driven spring. Created once per carousel and reused for every settle:
 * keeping one instance alive is what lets a new gesture inherit the velocity of
 * the settle it interrupted.
 *
 * `now` is injectable so tests can drive the clock rather than wait on frames.
 */
export const createSpringDriver = (
  { onFrame, onRest, immediate, responseMs = RESPONSE_MS }: DriverOptions,
  scheduleFrame: (cb: (t: number) => void) => number = requestAnimationFrame,
  cancelFrame: (id: number) => void = cancelAnimationFrame
): SpringDriver => {
  let state: SpringState = { x: 0, v: 0 };
  let target = 0;
  let frame: number | null = null;
  let lastT = 0;

  const stop = () => {
    if (frame !== null) cancelFrame(frame);
    frame = null;
  };

  const tick = (t: number) => {
    frame = null;
    // First frame of a run has no previous timestamp to diff against; a 16ms
    // assumption is closer than 0 (which would stall) or `t` (a huge number).
    const elapsed = lastT === 0 ? 16 : t - lastT;
    lastT = t;
    state = stepSpring(state, target, elapsed, responseMs);

    if (isSpringAtRest(state, target)) {
      state = { x: target, v: 0 };
      lastT = 0;
      onFrame(target);
      onRest(target);
      return;
    }

    onFrame(state.x);
    frame = scheduleFrame(tick);
  };

  const start = () => {
    if (frame !== null) return;
    lastT = 0;
    frame = scheduleFrame(tick);
  };

  return {
    setTarget: (next: number) => {
      target = next;
      if (immediate?.()) {
        stop();
        state = { x: next, v: 0 };
        lastT = 0;
        onFrame(next);
        onRest(next);
        return;
      }
      if (isSpringAtRest(state, target)) {
        stop();
        state = { x: next, v: 0 };
        lastT = 0;
        onFrame(next);
        onRest(next);
        return;
      }
      start();
    },
    setPosition: (x: number, v = 0) => {
      stop();
      state = { x, v };
      lastT = 0;
    },
    position: () => state.x,
    velocity: () => state.v,
    running: () => frame !== null,
    stop
  };
};
